import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface Achievement {
  id: string;
  title: string;
  description: string;
  points: number;
  isUnlocked: boolean;
  progress: number;
  target: number;
  type: 'habit' | 'streak' | 'milestone' | 'challenge';
  category: 'daily' | 'savings' | 'transactions' | 'engagement';
  streak?: number;
  lastCompleted?: string;
}

interface GamificationContextType {
  level: number;
  xp: number;
  nextLevelXp: number;
  achievements: Achievement[];
  unlockedRewards: string[];
  addXp: (amount: number, skipAchievementCheck?: boolean) => Promise<void>;
  checkAchievements: () => Promise<void>;
  unlockReward: (rewardId: string) => Promise<void>;
}

const DEFAULT_ACHIEVEMENTS: Achievement[] = [
  // Daily Habit Achievements
  {
    id: 'daily_login',
    title: 'Daily Login',
    description: 'Open the app for 7 consecutive days',
    points: 50,
    isUnlocked: false,
    progress: 0,
    target: 7,
    type: 'habit',
    category: 'daily',
    streak: 0,
    lastCompleted: undefined
  },
  {
    id: 'morning_check',
    title: 'Morning Check',
    description: 'Check your finances first thing in the morning',
    points: 30,
    isUnlocked: false,
    progress: 0,
    target: 5,
    type: 'habit',
    category: 'daily',
    streak: 0,
    lastCompleted: undefined
  },

  // Savings Streak Achievements
  {
    id: 'savings_streak',
    title: 'Savings Streak',
    description: 'Save money for 30 consecutive days',
    points: 200,
    isUnlocked: false,
    progress: 0,
    target: 30,
    type: 'streak',
    category: 'savings',
    streak: 0,
    lastCompleted: undefined
  },
  {
    id: 'weekly_saver',
    title: 'Weekly Saver',
    description: 'Save money every week for a month',
    points: 150,
    isUnlocked: false,
    progress: 0,
    target: 4,
    type: 'streak',
    category: 'savings',
    streak: 0,
    lastCompleted: undefined
  },

  // Transaction Milestones
  {
    id: 'transaction_master',
    title: 'Transaction Master',
    description: 'Record 100 transactions',
    points: 100,
    isUnlocked: false,
    progress: 0,
    target: 100,
    type: 'milestone',
    category: 'transactions'
  },
  {
    id: 'category_expert',
    title: 'Category Expert',
    description: 'Use all expense categories',
    points: 75,
    isUnlocked: false,
    progress: 0,
    target: 8,
    type: 'milestone',
    category: 'transactions'
  },

  // Engagement Challenges
  {
    id: 'budget_planner',
    title: 'Budget Planner',
    description: 'Create and stick to a budget for 2 weeks',
    points: 150,
    isUnlocked: false,
    progress: 0,
    target: 14,
    type: 'challenge',
    category: 'engagement',
    streak: 0,
    lastCompleted: undefined
  },
  {
    id: 'financial_analyst',
    title: 'Financial Analyst',
    description: 'Review your spending patterns weekly for a month',
    points: 200,
    isUnlocked: false,
    progress: 0,
    target: 4,
    type: 'challenge',
    category: 'engagement',
    streak: 0,
    lastCompleted: undefined
  }
];

const GamificationContext = createContext<GamificationContextType | undefined>(undefined);

export const GamificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { toast } = useToast();
  const [level, setLevel] = useState(1);
  const [xp, setXp] = useState(0);
  const [nextLevelXp, setNextLevelXp] = useState(100);
  const [achievements, setAchievements] = useState<Achievement[]>(DEFAULT_ACHIEVEMENTS);
  const [unlockedRewards, setUnlockedRewards] = useState<string[]>([]);

  useEffect(() => {
    loadGamificationData();
  }, []);

  const loadGamificationData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('Loading gamification data for user:', user.id);

      const { data: gamificationData, error } = await supabase
        .from('user_gamification')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // If no data exists, create initial gamification data
        if (error.code === 'PGRST116') {
          console.log('Initializing gamification data for new user');
          const { data: newData, error: insertError } = await supabase
            .from('user_gamification')
            .insert({
              user_id: user.id,
              level: 1,
              xp: 0,
              next_level_xp: 100,
              achievements: DEFAULT_ACHIEVEMENTS,
              unlocked_rewards: []
            })
            .select()
            .single();

          if (insertError) throw insertError;

          setLevel(1);
          setXp(0);
          setNextLevelXp(100);
          setAchievements(DEFAULT_ACHIEVEMENTS);
          setUnlockedRewards([]);

          // Check achievements after initialization
          await checkAchievements();
          return;
        }
        throw error;
      }

      if (gamificationData) {
        console.log('Loaded gamification data:', gamificationData);
        setLevel(gamificationData.level || 1);
        setXp(gamificationData.xp || 0);
        setNextLevelXp(gamificationData.next_level_xp || 100);
        
        // Ensure all default achievements are present
        const mergedAchievements = DEFAULT_ACHIEVEMENTS.map(defaultAchievement => {
          const existingAchievement = gamificationData.achievements?.find(
            (a: Achievement) => a.id === defaultAchievement.id
          );
          return {
            ...defaultAchievement,
            ...existingAchievement,
            isUnlocked: existingAchievement?.isUnlocked || false,
            progress: existingAchievement?.progress || 0
          };
        });

        setAchievements(mergedAchievements);
        setUnlockedRewards(gamificationData.unlocked_rewards || []);

        // Check achievements after loading data
        await checkAchievements();
      }
    } catch (error) {
      console.error('Error loading gamification data:', error);
      toast({
        title: "Error",
        description: "Failed to load gamification data",
        variant: "destructive",
      });
    }
  };

  const addXp = async (amount: number, skipAchievementCheck: boolean = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const newXp = xp + amount;
      let newLevel = level;
      let newNextLevelXp = nextLevelXp;

      // Check for level up
      while (newXp >= newNextLevelXp) {
        newLevel++;
        newNextLevelXp = Math.floor(newNextLevelXp * 1.5);
      }

      // Update all gamification data
      const { error } = await supabase
        .from('user_gamification')
        .update({
          level: newLevel,
          xp: newXp,
          next_level_xp: newNextLevelXp,
          achievements: achievements,
          unlocked_rewards: unlockedRewards
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setLevel(newLevel);
      setXp(newXp);
      setNextLevelXp(newNextLevelXp);

      if (newLevel > level) {
        toast({
          title: "Level Up!",
          description: `Congratulations! You've reached level ${newLevel}!`,
          duration: 5000,
        });
      }

      // Only check achievements if not skipped
      if (!skipAchievementCheck) {
        await checkAchievements();
      }
    } catch (error) {
      console.error('Error adding XP:', error);
      toast({
        title: "Error",
        description: "Failed to add XP",
        variant: "destructive",
      });
    }
  };

  const checkAchievements = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's wallet data
      const { data: wallets, error: walletsError } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id);

      if (walletsError) throw walletsError;

      // Get user's transaction data
      const { data: transactions, error: transactionsError } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id);

      if (transactionsError) throw transactionsError;

      // Get user's daily activity
      const { data: dailyActivity, error: activityError } = await supabase
        .from('user_activity')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(30);

      if (activityError) throw activityError;

      // Track newly unlocked achievements
      const newlyUnlocked: Achievement[] = [];

      // Check achievements based on various criteria
      const newAchievements = achievements.map(achievement => {
        let progress = 0;
        let isUnlocked = false;
        let streak = achievement.streak || 0;
        let lastCompleted = achievement.lastCompleted;

        switch (achievement.id) {
          // Daily Habit Achievements
          case 'daily_login':
            const loginStreak = calculateStreak(dailyActivity?.filter(a => a.type === 'login') || []);
            progress = loginStreak;
            isUnlocked = loginStreak >= 7;
            streak = loginStreak;
            break;

          case 'morning_check':
            const morningChecks = dailyActivity?.filter(a => 
              a.type === 'morning_check' && 
              new Date(a.created_at).getHours() < 12
            ).length || 0;
            progress = morningChecks;
            isUnlocked = morningChecks >= 5;
            break;

          // Savings Streak Achievements
          case 'savings_streak':
            const savingsStreak = calculateSavingsStreak(transactions || []);
            progress = savingsStreak;
            isUnlocked = savingsStreak >= 30;
            streak = savingsStreak;
            break;

          case 'weekly_saver':
            const weeklySaves = calculateWeeklySaves(transactions || []);
            progress = weeklySaves;
            isUnlocked = weeklySaves >= 4;
            streak = weeklySaves;
            break;

          // Transaction Milestones
          case 'transaction_master':
            progress = transactions?.length || 0;
            isUnlocked = (transactions?.length || 0) >= 100;
            break;

          case 'category_expert':
            const uniqueCategories = new Set(transactions?.map(t => t.category) || []);
            progress = uniqueCategories.size;
            isUnlocked = uniqueCategories.size >= 8;
            break;

          // Engagement Challenges
          case 'budget_planner':
            const budgetDays = dailyActivity?.filter(a => a.type === 'budget_review').length || 0;
            progress = budgetDays;
            isUnlocked = budgetDays >= 14;
            streak = budgetDays;
            break;

          case 'financial_analyst':
            const weeklyReviews = calculateWeeklyReviews(dailyActivity || []);
            progress = weeklyReviews;
            isUnlocked = weeklyReviews >= 4;
            streak = weeklyReviews;
            break;
        }

        const currentAchievement = achievements.find(a => a.id === achievement.id);
        const wasUnlocked = currentAchievement?.isUnlocked || false;
        const newUnlockState = isUnlocked || wasUnlocked;

        // If newly unlocked, add to the list
        if (!wasUnlocked && isUnlocked) {
          newlyUnlocked.push({
            ...achievement,
            progress,
            isUnlocked: true,
            streak,
            lastCompleted: new Date().toISOString()
          });
        }

        return {
          ...achievement,
          progress,
          isUnlocked: newUnlockState,
          streak,
          lastCompleted
        };
      });

      // Update all gamification data in the database
      const { error: updateError } = await supabase
        .from('user_gamification')
        .update({
          level,
          xp,
          next_level_xp: nextLevelXp,
          achievements: newAchievements,
          unlocked_rewards: unlockedRewards
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setAchievements(newAchievements);

      // Show notifications and award XP for newly unlocked achievements
      let totalXpToAdd = 0;
      newlyUnlocked.forEach(achievement => {
        totalXpToAdd += achievement.points;
        toast({
          title: "Achievement Unlocked!",
          description: `${achievement.title}: ${achievement.description}`,
          duration: 5000,
        });
      });

      // Add XP for all newly unlocked achievements at once
      if (totalXpToAdd > 0) {
        await addXp(totalXpToAdd, true);
      }
    } catch (error) {
      console.error('Error checking achievements:', error);
      toast({
        title: "Error",
        description: "Failed to check achievements",
        variant: "destructive",
      });
    }
  };

  // Helper functions for streak calculations
  const calculateStreak = (activities: any[]) => {
    if (!activities.length) return 0;
    
    let streak = 1;
    let currentDate = new Date(activities[0].created_at);
    
    for (let i = 1; i < activities.length; i++) {
      const nextDate = new Date(activities[i].created_at);
      const diffDays = Math.floor((currentDate.getTime() - nextDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        streak++;
      } else if (diffDays > 1) {
        break;
      }
      
      currentDate = nextDate;
    }
    
    return streak;
  };

  const calculateSavingsStreak = (transactions: any[]) => {
    if (!transactions.length) return 0;
    
    const savings = transactions.filter(t => t.type === 'savings');
    if (!savings.length) return 0;
    
    return calculateStreak(savings);
  };

  const calculateWeeklySaves = (transactions: any[]) => {
    if (!transactions.length) return 0;
    
    const savings = transactions.filter(t => t.type === 'savings');
    const weeks = new Set(savings.map(t => 
      new Date(t.created_at).toISOString().slice(0, 10)
    ));
    
    return weeks.size;
  };

  const calculateWeeklyReviews = (activities: any[]) => {
    if (!activities.length) return 0;
    
    const reviews = activities.filter(a => a.type === 'weekly_review');
    const weeks = new Set(reviews.map(a => 
      new Date(a.created_at).toISOString().slice(0, 10)
    ));
    
    return weeks.size;
  };

  const unlockReward = async (rewardId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_gamification')
        .upsert({
          user_id: user.id,
          unlocked_rewards: [...unlockedRewards, rewardId],
        });

      if (error) throw error;

      setUnlockedRewards([...unlockedRewards, rewardId]);
    } catch (error) {
      console.error('Error unlocking reward:', error);
    }
  };

  return (
    <GamificationContext.Provider
      value={{
        level,
        xp,
        nextLevelXp,
        achievements,
        unlockedRewards,
        addXp,
        checkAchievements,
        unlockReward,
      }}
    >
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (context === undefined) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
}; 